import React, { useState } from 'react';
import {
  Box,
  TableRow,
  TableCell,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { Info, Close, Delete } from '@mui/icons-material';
import { k8sApi } from '@common-reducers/backend-api/backend-k8s-api';
import { SessionDetailsCube } from './session-details-cube';
import { extractMachineType } from './k8s-analyzer';
import { ContainerState, K8sPod, PodTableRowProps } from './k8s-analyzer-types';

const getContainerStateString = (state: ContainerState): string => {
  if (!state) return 'Unknown';
  if (state.running) return 'Running';
  if (state.waiting?.reason) return state.waiting.reason;
  if (state.terminated?.reason) return state.terminated.reason;
  return 'Unknown';
};

const getStatusColor = (status: string | undefined | null) => {
  if (!status || typeof status !== 'string') return '#9e9e9e';
  
  switch (status.toLowerCase()) {
    case 'running':
      return 'var(--bulk-success)'; // green
    case 'completed':
      return '#2196f3'; // blue
    case 'containerstatus':
    case 'containerstatusunknown':
      return 'var(--bulk-warning)'; // orange
    case 'error':
    case 'error↓':
    case 'oomkilled':
    case 'crashloopbackoff':
      return '#d32f2f'; // darker red
    case 'terminating':
      return '#9c27b0'; // purple
    default:
      return '#9e9e9e'; // grey
  }
};

const getStatusChip = (status: string | undefined | null) => {
  const displayStatus = status && typeof status === 'string' ? status : 'Unknown';
  return (
    <Box
      component="span"
      sx={{
        px: 2,
        py: 0.5,
        borderRadius: 1,
        bgcolor: getStatusColor(status),
        color: 'white',
        display: 'inline-block'
      }}
    >
      {displayStatus}
    </Box>
  );
};

export const PodTableRow: React.FC<PodTableRowProps> = ({ pod, onViewDetails, onPodDeleted }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletePod] = k8sApi.useDeletePodByIdActionMutation();

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deletePod(pod.name);
      onPodDeleted && onPodDeleted();
    } catch (err) {
      console.error('Failed to delete pod:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <TableRow
      sx={{
        '&:last-child td, &:last-child th': { border: 0 },
        backgroundColor: pod.nodeName ? 'inherit' : 'error.light'
      }}
    >
      <TableCell component="th" scope="row">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="body2">
            {pod.name}
          </Typography>
          {pod.nodeName && (
            <Typography variant="caption" color="text.secondary">
              Node: {pod.nodeName}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell align="center">
        {getStatusChip(pod.statusString)}
      </TableCell>
      <TableCell align="center">
        <Chip
          label={pod.readyState}
          color={pod.readyState === '2/2' ? 'success' : 'warning'}
          size="small"
          variant="outlined"
        />
      </TableCell>
      <TableCell align="center">
        <Chip
          label={pod.version}
          color="primary"
          size="small"
          variant="outlined"
        />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {pod.containerStatuses.map((cs, index) => (
            <Box key={cs.containerID || index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ minWidth: 100 }}>
                Container {index + 1}:
              </Typography>
              <Chip
                size="small"
                label={getContainerStateString(cs.state)}
                color={cs.ready ? 'success' : 'warning'}
                variant="outlined"
              />
              {cs.restartCount > 0 && (
                <Chip
                  size="small"
                  label={`Restarts: ${cs.restartCount}`}
                  color="error"
                  variant="outlined"
                />
              )}
            </Box>
          ))}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {pod.sessions && pod.sessions.length > 0 ? (
            pod.sessions.map(session => (
              <Box key={session._id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  size="small"
                  label={session.status}
                  color={session.status === 'connected' ? 'success' : 'warning'}
                  variant="outlined"
                />
                {session.user && (
                  <Typography variant="body2" noWrap>
                    {session.user.email}
                  </Typography>
                )}
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No active sessions
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => onViewDetails(pod)}
            >
              <Info />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Pod">
            <IconButton
              size="small"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <CircularProgress size={20} />
              ) : (
                <Delete />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default PodTableRow; 